import * as React from 'react';
import { Box, Button, Divider, IconButton, Skeleton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CodeMirror from '@uiw/react-codemirror';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { languages } from '@codemirror/language-data';
import { useSchemaReadme } from '../../hooks/useSchemaReadme';
import { MarkdownBoxStyles } from '../../@shared/markdown';
import { FlexBoxColumn, FlexBoxInline, CodeMirrorTheme, TabSx, PrimaryBtnSx, SecondaryBtnSx } from '../../@shared/templates';
import { Palette, Sizes } from '../../@shared/palette';
import { ButtonComponent } from '../../components/ButtonComponent';

export type TabsContentReadmeBaseProps = {
  schemaId: string
  isHasDescription: boolean
}

export type TabsContentReadmeProps = TabsContentReadmeBaseProps & {
  isVisible: boolean
}

const TabsContentReadme = (props: TabsContentReadmeProps) => {
  const { content, isLoading, isProcessing, onChangeContent, onLoadSchemaReadme, onUpdateSchemaReadme } = useSchemaReadme();
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [originalContent, setOriginalContent] = React.useState("");
  const [editTabIndex, setEditTabIndex] = React.useState(0);

  React.useEffect(() => {
    if (props.schemaId) {
      onLoadSchemaReadme(props.schemaId);
    }
  }, [props.schemaId, onLoadSchemaReadme]);

  React.useEffect(() => {
    if (!isLoading && content && window.location.hash) {
      const interval = setInterval(() => {
        const el = document.querySelector(window.location.hash);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
          clearInterval(interval);
        }
      }, 200);

      const timeout = setTimeout(() => clearInterval(interval), 5000);

      return () => {
        window.clearInterval(interval);
        window.clearTimeout(timeout);
      };
    }
  }, [isLoading, content]);

  const handleEnterEditMode = React.useCallback(() => {
    setOriginalContent(content);
    setIsEditMode(true);
  }, [content]);

  const handleCancel = React.useCallback(() => {
    onChangeContent(originalContent);
    setIsEditMode(false);
    setEditTabIndex(0);
  }, [originalContent, onChangeContent]);

  const handleSave = React.useCallback(async () => {
    await onUpdateSchemaReadme(props.schemaId, content);
    setIsEditMode(false);
  }, [props.schemaId, content, onUpdateSchemaReadme]);

  const flatten = (e: string, c: any): any => {
    return typeof c === 'string'
      ? e + c
      : React.Children.toArray(c.props.children).reduce(flatten, e)
  }

  const HeadingRenderer = (props: any) => {
    const children = React.Children.toArray(props.children)
    const text = children.reduce(flatten, '')
    const slug = text.toLowerCase().replace(/\W/g, '-')

    return React.createElement(props.node.tagName, { id: slug }, props.children)
  }

  if (isLoading) {
    return (
      <div hidden={!props.isVisible}>
        <Box sx={{ ...FlexBoxColumn, width: "100%", padding: "10px" }}>
          <Box sx={{
            width: "100%"
          }}>
            <Skeleton variant="text" width="60%" sx={{ fontSize: Sizes.pageTitle, marginBottom: "15px" }} />
            <Skeleton variant="text" width="100%" sx={{ fontSize: Sizes.textPrimary, marginBottom: "5px" }} />
            <Skeleton variant="text" width="95%" sx={{ fontSize: Sizes.textPrimary, marginBottom: "5px" }} />
            <Skeleton variant="text" width="80%" sx={{ fontSize: Sizes.textPrimary, marginBottom: "20px" }} />
            <Skeleton variant="text" width="40%" sx={{ fontSize: Sizes.pageSecondTitle, marginBottom: "10px" }} />
            <Skeleton variant="text" width="100%" sx={{ fontSize: Sizes.textPrimary, marginBottom: "5px" }} />
            <Skeleton variant="text" width="90%" sx={{ fontSize: Sizes.textPrimary, marginBottom: "5px" }} />
            <Skeleton variant="text" width="70%" sx={{ fontSize: Sizes.textPrimary, marginBottom: "20px" }} />
            <Skeleton variant="rectangular" width="100%" height={80} sx={{ marginBottom: "15px", borderRadius: "5px" }} />
            <Skeleton variant="text" width="50%" sx={{ fontSize: Sizes.textPrimary, marginBottom: "5px" }} />
            <Skeleton variant="text" width="85%" sx={{ fontSize: Sizes.textPrimary }} />
          </Box>
        </Box>
      </div>
    );
  }

  return (
    <Box hidden={!props.isVisible} sx={{ overflow: "scroll", height: `calc(100vh - ${props.isHasDescription ? 260 : 240}px)` }}>
      {!isEditMode ? (
        <Box sx={{
          position: "relative",
          width: "100%",
          "&:hover .edit-button": {
            opacity: 1
          }
        }}>
          <Box
            className="edit-button"
            sx={{
              position: "absolute",
              top: "10px",
              right: "10px",
              opacity: 0,
              transition: "opacity 0.2s ease-in-out",
              zIndex: 1
            }}
          >
            <Tooltip title="Edit Readme">
              <IconButton sx={{ ...SecondaryBtnSx, p: "5px", border: "none" }} onClick={handleEnterEditMode} size="small">
                <EditIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{
            ...MarkdownBoxStyles,
            width: "100%",
            minHeight: "200px",
            padding: "10px"
          }}>
            {content ? (
              <Markdown components={{ h1: HeadingRenderer, h2: HeadingRenderer, h3: HeadingRenderer }} remarkPlugins={[remarkGfm]}>{content}</Markdown>
            ) : (
              <Typography sx={{ color: Palette.textColorSecondary, fontStyle: "italic" }}>
                No Readme content. Click the edit button to add content.
              </Typography>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ ...FlexBoxColumn, width: "100%", height: "100%" }}>
          <Box sx={{ ...FlexBoxInline, justifyContent: "space-between", width: "100%", marginBottom: "10px" }}>
            <Box>
              <Tabs value={editTabIndex} onChange={(_, newValue) => setEditTabIndex(newValue)}>
                <Tab label="Editor" sx={TabSx} />
                <Tab label="Preview" sx={TabSx} />
              </Tabs>
              <Divider sx={{ marginBottom: "20px" }} />
            </Box>
            <Box sx={{ ...FlexBoxInline, gap: "10px" }}>
              <Button
                sx={{ ...SecondaryBtnSx, border: "none" }}
                onClick={handleCancel}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <ButtonComponent
                sx={PrimaryBtnSx}
                onClick={handleSave}
                disabled={isProcessing}
                text='Save'
                isProcessing={isProcessing}
              />
            </Box>
          </Box>
          <Box hidden={editTabIndex !== 0} sx={{ width: "100%", minHeight: "400px" }}>
            <Box sx={{
              ...FlexBoxColumn,
              width: "100%",
              border: Palette.borderPrimary,
              borderRadius: "5px",
              overflow: "hidden"
            }}>
              <Box sx={{ width: "100%", height: "100%", overflow: "auto" }}>
                <CodeMirror
                  value={content}
                  height="100%"
                  minHeight="400px"
                  theme={CodeMirrorTheme}
                  extensions={[markdown({ base: markdownLanguage, codeLanguages: languages })]}
                  onChange={(value) => onChangeContent(value)}
                  style={{ fontSize: Sizes.textPrimary }}
                />
              </Box>
            </Box>
          </Box>
          <Box hidden={editTabIndex !== 1} sx={{ width: "100%", minHeight: "400px" }}>
            <Box sx={{
              ...FlexBoxColumn,
              width: "100%",
              overflow: "hidden"
            }}>
              <Box sx={{
                ...MarkdownBoxStyles,
                width: "100%",
                height: "100%",
                overflow: "auto",
                padding: "10px"
              }}>
                {content ? (
                  <Markdown remarkPlugins={[remarkGfm]}>{content}</Markdown>
                ) : (
                  <Typography sx={{ color: Palette.textColorSecondary, fontStyle: "italic" }}>
                    Start typing to see preview...
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default TabsContentReadme;
